/**
 * resumeOffer.ts — RESUME MISSION OFFER (BUG FREEZE™ Stage 2, Build 2.15.0)
 *
 * Bug fix for startup session restoration: opening WorkRoamer must land on
 * Home first. When Continue Where You Left Off™ has a resume target that is
 * NOT Home, it is converted into an *offer* the operator explicitly accepts
 * from a small banner — never an automatic navigation into an old Mission
 * screen or workspace.
 *
 * sessionStorage-scoped (per tab, per boot); consume-once semantics.
 */

export const RESUME_OFFER_KEY = 'nomad:resume-offer:v1';

export interface ResumeOffer {
  route: string;
  scrollY: number;
  offeredAt: string;
}

/** Routes that never warrant an offer — landing there IS the default. */
const HOME_ROUTES = new Set(['/', '/home', '/dashboard']);

export function shouldOfferResume(route: string): boolean {
  return typeof route === 'string' && route.startsWith('/') && !HOME_ROUTES.has(route);
}

export function storeResumeOffer(route: string, scrollY: number, now: Date = new Date()): void {
  if (!shouldOfferResume(route)) return;
  try {
    sessionStorage.setItem(RESUME_OFFER_KEY, JSON.stringify({
      route, scrollY: Math.max(0, scrollY || 0), offeredAt: now.toISOString(),
    } satisfies ResumeOffer));
  } catch { /* storage unavailable — offer silently skipped */ }
}

export function peekResumeOffer(): ResumeOffer | null {
  try {
    const raw = sessionStorage.getItem(RESUME_OFFER_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw) as unknown;
    if (p == null || typeof p !== 'object') return null;
    const o = p as Record<string, unknown>;
    if (typeof o.route !== 'string' || !o.route.startsWith('/')) return null;
    return {
      route: o.route,
      scrollY: typeof o.scrollY === 'number' && o.scrollY >= 0 ? o.scrollY : 0,
      offeredAt: typeof o.offeredAt === 'string' ? o.offeredAt : new Date(0).toISOString(),
    };
  } catch { return null; }
}

/** Remove the offer (accepted or dismissed) — it never re-appears this boot. */
export function clearResumeOffer(): void {
  try { sessionStorage.removeItem(RESUME_OFFER_KEY); } catch { /* ignore */ }
}

/** Human label for the offer banner, derived from the route. */
export function resumeOfferLabel(route: string): string {
  const seg = route.split('?')[0].split('/').filter(Boolean);
  if (seg.length === 0) return 'your last screen';
  return seg[seg.length - 1]
    .split('-')
    .map(w => (w.length > 0 ? w[0].toUpperCase() + w.slice(1) : w))
    .join(' ');
}
